import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Heading, LoadingSpinner, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { marketingService } from '../../services/marketing'
import type { MarketingGame, MarketingGameSession } from '../../types/marketing'
import { buildOrderSquares, formatDateBr, formatGameTime, rankingPlaceLabel } from './gamePuzzle'

function sessionStatusLabel(session: MarketingGameSession): string {
  return session.status === 'completed' ? 'Concluiu' : 'Desistiu'
}

function sessionStatusClass(session: MarketingGameSession): string {
  return session.status === 'completed'
    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
}

export interface GameRankingTabProps {
  /** Limite de linhas (ex.: 10 na home) */
  limit?: number
  /** Oculta coluna Data */
  hideDate?: boolean
  /** Oculta coluna Status */
  hideStatus?: boolean
  /** Oculta a sequência de palavras encontradas */
  hideWordSequence?: boolean
  /** Só exibe tempos se o usuário logado já tiver jogado esta edição */
  hideTimeUntilCurrentUserPlayed?: boolean
}

const GameRankingTab: React.FC<GameRankingTabProps> = ({
  limit,
  hideDate = false,
  hideStatus = false,
  hideWordSequence = false,
  hideTimeUntilCurrentUserPlayed = false,
}) => {
  const { user } = useAuthContext()
  const [games, setGames] = useState<MarketingGame[]>([])
  const [selectedGameId, setSelectedGameId] = useState<string>('')
  const [sessions, setSessions] = useState<MarketingGameSession[]>([])
  const [loadingGames, setLoadingGames] = useState(true)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadGames = useCallback(async () => {
    try {
      setLoadingGames(true)
      setError(null)
      const data = await marketingService.listGames()
      setGames(data)
      if (data.length > 0) {
        const today = new Date().toISOString().slice(0, 10)
        const active =
          data.find((g) => g.is_active && g.start_date <= today && g.end_date >= today) || data[0]
        setSelectedGameId(active.id)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar jogos')
    } finally {
      setLoadingGames(false)
    }
  }, [])

  useEffect(() => {
    void loadGames()
  }, [loadGames])

  useEffect(() => {
    if (!selectedGameId) {
      setSessions([])
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        setLoadingSessions(true)
        setError(null)
        const list = await marketingService.listGameSessions(selectedGameId)
        if (!cancelled) setSessions(list)
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar ranking')
          setSessions([])
        }
      } finally {
        if (!cancelled) setLoadingSessions(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [selectedGameId])

  const selectedGame = games.find((g) => g.id === selectedGameId) || null

  const currentUserPlayed = useMemo(
    () => Boolean(user?.id && sessions.some((s) => s.user_id === user.id)),
    [sessions, user?.id]
  )

  const showTimeColumn = !hideTimeUntilCurrentUserPlayed || currentUserPlayed
  const visibleSessions = typeof limit === 'number' ? sessions.slice(0, limit) : sessions
  const colSpan =
    2 +
    (hideStatus ? 0 : 1) +
    (hideWordSequence ? 0 : 1) +
    (showTimeColumn ? 1 : 0) +
    (hideDate ? 0 : 1)

  if (loadingGames) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  let completedRank = 0

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Ranking
          </Heading>
          <Text size={typeof limit === 'number' ? 'xs' : 'sm'} color="muted" className="mt-1">
            Quem concluiu (por tempo) e quem desistiu
            {hideWordSequence ? '.' : ', com a ordem das palavras encontradas.'} Cada pessoa tem uma
            única tentativa.
            {typeof limit === 'number' ? ` Top ${limit}.` : ''}
          </Text>
        </div>
        <div className="w-full sm:w-80">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Jogo
          </label>
          <select
            value={selectedGameId}
            onChange={(e) => setSelectedGameId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          >
            {games.length === 0 && <option value="">Nenhum jogo</option>}
            {games.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
                {!g.is_active ? ' (inativo)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedGame && (
        <Text size="sm" color="muted" className="mb-4">
          Período: {formatDateBr(selectedGame.start_date)} - {formatDateBr(selectedGame.end_date)} ·{' '}
          {selectedGame.questions.length} palavras
        </Text>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {!showTimeColumn && hideTimeUntilCurrentUserPlayed && (
        <Text size="xs" color="muted" className="mb-3">
          Os tempos aparecem depois que você jogar esta edição.
        </Text>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        {loadingSessions ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Jogador
                  </th>
                  {!hideStatus && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                  )}
                  {!hideWordSequence && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Ordem
                    </th>
                  )}
                  {showTimeColumn && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Tempo
                      <span className="mt-0.5 block text-[10px] font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                        mm:ss:mmm
                      </span>
                    </th>
                  )}
                  {!hideDate && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Data
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleSessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colSpan}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhuma partida registrada neste jogo.
                    </td>
                  </tr>
                ) : (
                  visibleSessions.map((entry) => {
                    const isMe = entry.user_id === user?.id
                    const isCompleted = entry.status === 'completed'
                    if (isCompleted) completedRank += 1
                    const rankLabel = isCompleted ? rankingPlaceLabel(completedRank) : '—'
                    const when = entry.completed_at || entry.started_at
                    const orderLabel = buildOrderSquares(entry.word_order)

                    return (
                      <tr
                        key={entry.id}
                        className={
                          isMe
                            ? 'bg-teal-50 dark:bg-teal-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900/20'
                        }
                      >
                        <td className="px-4 py-3 text-sm font-extrabold text-[#016B76] dark:text-teal-300">
                          {rankLabel}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {entry.user_name}
                          {isMe ? ' (você)' : ''}
                        </td>
                        {!hideStatus && (
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${sessionStatusClass(entry)}`}
                            >
                              {sessionStatusLabel(entry)}
                            </span>
                          </td>
                        )}
                        {!hideWordSequence && (
                          <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-600 dark:text-gray-300">
                            {orderLabel || '—'}
                          </td>
                        )}
                        {showTimeColumn && (
                          <td className="px-4 py-3 text-sm font-semibold tabular-nums whitespace-nowrap text-gray-800 dark:text-gray-100">
                            {entry.time_seconds != null
                              ? formatGameTime(entry.time_seconds)
                              : '—'}
                          </td>
                        )}
                        {!hideDate && (
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {when ? new Date(when).toLocaleString('pt-BR') : '—'}
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default GameRankingTab
